import { Injectable, NgZone } from '@angular/core';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { HardwareService } from './hardware.service';
import { CashJournalService, CashJournal, CashOpenType, CashOpenOrigin } from './cash-journal.service';
import { PopupService } from './popup.service';
import { AuthService } from './auth.service';
import { MatDialog } from '@angular/material/dialog';
import { CashAmountDialogComponent } from '../shared/cash-amount-dialog/cash-amount-dialog.component';
import { F8LinkConfirmDialogComponent } from '../shared/f8-link-confirm-dialog/f8-link-confirm-dialog.component';
import { OrderContextService } from './order-context.service';
import { OrderService } from './order.service';
@Injectable({
    providedIn: 'root'
})
export class ShortcutService {

    constructor(
        private hardwareService: HardwareService,
        private cashJournalService: CashJournalService,
        private popupService: PopupService,
        private authService: AuthService,
        private ngZone: NgZone,
        private dialog: MatDialog,
        private orderContextService: OrderContextService,
        private orderService: OrderService,
        private router: Router
    ) { }

    init() {
        window.addEventListener('keydown', (event) => {
            // F8 Shortcut
            if (event.key === 'F8') {
                event.preventDefault();
                this.handleF8Shortcut();
            }

            // Ctrl + Shift + O
            if (event.ctrlKey && event.shiftKey && (event.key === 'o' || event.key === 'O')) {
                event.preventDefault();
                this.handleOpenDrawer(CashOpenOrigin.BUTTON);
            }
        });
    }

    private handleF8Shortcut() {
        if (!this.authService.isAuthenticated()) return;

        this.ngZone.run(() => {
            const dialogRef = this.dialog.open(CashAmountDialogComponent, {
                width: '350px',
                disableClose: true,
                autoFocus: true
            });

            dialogRef.afterClosed().subscribe(async (amount: number | null) => {
                if (amount === null) return;

                const currentOrder = this.orderContextService.getCurrentOrder();

                // Calcul du total effectif :
                // - commande sauvegardée → netTotal du backend
                // - nouvelle commande (non sauvegardée) → calcul front depuis orderLines
                let effectiveTotal: number | null = null;
                if (currentOrder) {
                    if (currentOrder.netTotal != null && currentOrder.netTotal > 0) {
                        effectiveTotal = currentOrder.netTotal;
                    } else if (currentOrder.orderLines && currentOrder.orderLines.length > 0) {
                        effectiveTotal = currentOrder.calculateTotal() - (currentOrder.discount || 0);
                    }
                }

                if (currentOrder && effectiveTotal != null && effectiveTotal > 0 && Math.abs(amount - effectiveTotal) < 0.01) {
                    // Capturer avant d'ouvrir le dialog (l'ordre sera créé à l'intérieur du dialog)
                    const wasNewOrder = !currentOrder.id;

                    // Afficher le dialog de confirmation de lien
                    const linkDialogRef = this.dialog.open(F8LinkConfirmDialogComponent, {
                        width: '380px',
                        disableClose: true,
                        panelClass: 'f8-dialog-container',
                        exitAnimationDuration: '0ms',
                        data: { order: currentOrder, amount }
                    });

                    linkDialogRef.afterClosed().subscribe((result: number | null) => {
                        // null = annulé, 0 = F8 normal, >0 = orderId confirmé
                        if (result === null || result === undefined) return;

                        if (result > 0) {
                            // Navigation immédiate si c'était une nouvelle commande
                            if (wasNewOrder) {
                                this.router.navigate(['/orderDetails'], { queryParams: { id: result } });
                            }
                            // skipVirtual=wasNewOrder : commande créée avec isVirtual:true, makeVirtual inutile
                            this.performF8Link(result, amount, false, wasNewOrder);
                        } else {
                            // Refus — flux F8 standard : log d'abord, tiroir après confirmation
                            this.logF8ThenOpenDrawer(amount);
                        }
                    });
                } else {
                    // Pas de commande correspondante — flux standard : log d'abord, tiroir après confirmation
                    this.logF8ThenOpenDrawer(amount);
                }
            });
        });
    }

    private performF8Link(orderId: number, amount: number, navigateAfter: boolean = false, skipVirtual: boolean = false): void {
        const user = this.authService.getUser();

        // logAndLink = logEvent + confirmMatch en 1 seul appel backend
        const logAndLink$ = this.cashJournalService.logAndLink(
            orderId, amount,
            user ? user.username : 'UNKNOWN',
            'Ouverture via Raccourci F8 (Navigateur)'
        );

        // Pour les commandes existantes seulement : makeVirtual en parallèle
        const ops: any[] = skipVirtual
            ? [logAndLink$]
            : [logAndLink$, this.orderService.makeVirtual(orderId)];

        forkJoin(ops).subscribe({
            next: () => {
                // Tout confirmé en base → ouvrir le tiroir maintenant
                void this.hardwareService.openDrawerWithoutLog();
                if (navigateAfter) {
                    this.router.navigate(['/orderDetails'], { queryParams: { id: orderId } });
                }
                this.popupService.showSuccess(
                    `✅ F8 lié à la commande #${orderId} (${amount} DH)`,
                    3000
                );
            },
            error: (err) => {
                console.error('F8 link error', err);
                this.popupService.showError('Erreur réseau — F8 non enregistré, tiroir bloqué', 3000);
            }
        });
    }

    /**
     * Flux F8 normal (sans liaison) — garantit que le F8 est bien en base AVANT d'ouvrir le tiroir.
     * Si le log échoue (réseau), le tiroir reste fermé et l'utilisateur est alerté.
     */
    private logF8ThenOpenDrawer(amount: number): void {
        const user = this.authService.getUser();
        const journal: CashJournal = {
            type: CashOpenType.WITHOUT_ORDER,
            origin: CashOpenOrigin.SHORTCUT,
            username: user ? user.username : 'UNKNOWN',
            reason: 'Ouverture via Raccourci F8 (Navigateur)',
            amount: amount
        };
        this.cashJournalService.logEvent(journal).subscribe({
            next: () => {
                this.hardwareService.openDrawerWithoutLog().then(success => {
                    if (success) {
                        this.popupService.showSuccess(`F8: ${amount} DH enregistré et tiroir ouvert !`, 2000);
                    } else {
                        this.popupService.showError('F8 enregistré mais le tiroir ne s\'est pas ouvert', 3000);
                    }
                });
            },
            error: () => {
                this.popupService.showError('Erreur réseau — F8 non enregistré, tiroir bloqué', 4000);
            }
        });
    }

    private async handleOpenDrawer(origin: CashOpenOrigin) {
        if (!this.authService.isAuthenticated()) return;

        const success = await this.hardwareService.openDrawer(origin);

        if (!success) {
            console.warn('🚫 Drawer opening failed (Port may be used by another app or AHK)');
            return;
        }

        // Feedback in UI
        this.ngZone.run(() => {
            this.popupService.showSuccess('Tiroir-caisse ouvert !', 2000);
        });
    }
}
